// @flow
import * as React from 'react';
import { get, set } from 'lodash';
import { Formik } from 'formik';
import { APIContext } from 'speed-js-react';
import GrommetForm from 'speed-js-react-web/src/components/grommet2/forms/GrommetForm';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import { FormattedMessage } from 'react-intl';
import { Text } from 'grommet';
import useCreatablePersonField from 'esm-web-pro/hooks/useCreatablePersonField';
import cloneDeep from 'lodash/cloneDeep';
import memoizeOne from 'memoize-one';

type Props = {
    forwardRef: React.ElementRef<Formik>,
    // schema: Object,
    initial?: Object,
    onChange: Function,
    onInitialize: Function,
    actions?: Object,
    resourceName: string,
    objectId: string,
    resourceArguments: Array<string>,
    // eslint-disable-next-line react/no-unused-prop-types
    excludeFields?: Array<string>,
    schemaError?: Object,
    height: ?string,
    padding: ?string,
    overflow: ?string,
}

const applyExtraSchema = memoizeOne((schemaResult, extraSchema) => {
    if (!extraSchema) {
        return schemaResult;
    }

    const schema = cloneDeep(schemaResult);
    if (extraSchema) {
        // Each entry is an object where the keys are the path to the property in the schema
        // and the values are the values for that property
        extraSchema.forEach((entry) => {
            // For each object element in the array gets the key and value pairs
            // and sets the value for that property in the schema object.
            Object.entries(entry).forEach((e) => {
                const [path, value] = e;
                const delimiterIndex = path.indexOf('.');
                const fieldName = path.substring(0, delimiterIndex);
                const pathName = path.substring(delimiterIndex + 1);
                const field = schema.fields.find((schemaField) => schemaField[0] === fieldName);
                if (field) {
                    set(field[1], pathName, value);
                }
            });
        });
    }
    return schema;
});

const MapObjectFunctionalForm = (props: Props) => {
    const {
        onInitialize, forwardRef, onChange, actions, schemaError, height, padding, overflow,
        resourceName, resourceArguments, initial, excludeFields, objectId,
    } = props;

    const [schema, setSchema] = React.useState(null);
    const [initialValues, setInitialValues] = React.useState(null);

    const [extraSchema, formikRef, personCreator] = useCreatablePersonField('property_owner');

    React.useEffect(() => {
        if (onInitialize) {
            onInitialize();
        }
    }, []);

    const { apiClient } = React.useContext(APIContext);

    const getSchema = React.useCallback(() => (
        apiClient.resources[resourceName].schema(...resourceArguments).then((s) => {
            if (excludeFields) {
                s.fields = s.fields.filter((value) => excludeFields.indexOf(value[0]) === -1);
            }

            const initialObject = {};

            s.fields.forEach((schemaField) => {
                const [name, value] = schemaField;
                const initialValue = s.fields.find((field) => field[0] === name)[1].initial;
                if (initialValue !== undefined && initialValue !== null) {
                    initialObject[name] = initialValue;
                }
            });

            let initialData = {};
            if (initial && initialValues) {
                initialData = { ...initial, ...initialValues, ...initialObject };
            } else if (initial && !initialValues) {
                initialData = { ...initial, ...initialObject };
            } else if (!initial && initialValues) {
                initialData = { ...initialValues, ...initialObject };
            } else {
                initialData = initialObject;
            }

            setSchema(s);
            setInitialValues(initialData);
        })
    ), [apiClient, resourceName, initialValues, excludeFields, initial, resourceArguments]);

    React.useEffect(() => {
        getSchema();
    }, [apiClient, resourceName]);

    if (!schema && !schemaError) {
        return (
            <Spinner
                type="clipLoader"
                loading
                blocking={false}
                color="black"
                coverStyle={{
                    position: 'relative',
                    top: '20px',
                }}
            />
        );
    }

    if (!schema && schemaError) {
        return (
            <Text color="status-error" margin={{ top: 'small' }}>
                <FormattedMessage
                    id="mapobjectform-schema-error"
                    defaultMessage="The form could not be loaded due to an error."
                />
            </Text>
        );
    }

    return (
        <>
            <GrommetForm
                ref={forwardRef}
                schema={applyExtraSchema(schema, extraSchema)}
                initial={initial}
                nonFieldErrorsName="non_field_errors"
                height={height}
                overflow={overflow}
                padding={padding}
                onChange={onChange}
                actions={actions}
                extraSchema={extraSchema}
                forwardRef={formikRef}
                validateOnChange
            />
            {personCreator}
        </>
    );
};

MapObjectFunctionalForm.defaultProps = {
    initial: {},
    actions: null,
    excludeFields: undefined,
    schemaError: null,
    height: null,
    padding: null,
    overflow: null,
};

export default MapObjectFunctionalForm;
